import type { ReactNode, SVGProps } from 'react'

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number
}

const BaseIcon = ({
  children,
  size = 20,
  strokeWidth = 1.8,
  ...props
}: IconProps & { children: ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    {children}
  </svg>
)

export const HomeIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M3 11.5 12 4l9 7.5" />
    <path d="M5.5 10.8V20h13V10.8" />
  </BaseIcon>
)

export const UsersIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M16 21v-1.5a3.5 3.5 0 0 0-3.5-3.5h-3A3.5 3.5 0 0 0 6 19.5V21" />
    <circle cx="11" cy="8" r="3" />
    <path d="M18 9.8a2.5 2.5 0 1 1 0 5" />
  </BaseIcon>
)

export const PlusCircleIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8.2v7.6M8.2 12h7.6" />
  </BaseIcon>
)

/** Ícone "+" simples centralizado, sem círculo. */
export const PlusIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 5v14M5 12h14" />
  </BaseIcon>
)

export const CalendarIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M8 3.5v3M16 3.5v3M3.5 9.5h17" />
    <path d="M8.2 13.5h3.2M8.2 16.8h2.2M14 16.8h2" />
  </BaseIcon>
)

export const UserIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="8" r="3.2" />
    <path d="M5.2 20a6.8 6.8 0 0 1 13.6 0" />
  </BaseIcon>
)

export const SearchIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 3.8 3.8" />
  </BaseIcon>
)

export const TrendIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M4 18h16" />
    <path d="m6.5 14.5 3-3 3 2.6 4-5.1" />
    <path d="M16.8 6h2.7v2.8" />
  </BaseIcon>
)

export const AlertCircleIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 8.2v5.1" />
    <circle cx="12" cy="16.8" r="0.8" fill="currentColor" stroke="none" />
  </BaseIcon>
)

export const InfoIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11.1v4.6" />
    <circle cx="12" cy="8.1" r="0.8" fill="currentColor" stroke="none" />
  </BaseIcon>
)

export const QuestionMarkCircleIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9.5 9.2a2.5 2.5 0 0 1 4.2 1.8c0 1.7-1.7 2.5-2.5 2.5" />
    <circle cx="12" cy="16.8" r="0.8" fill="currentColor" stroke="none" />
  </BaseIcon>
)

export const PrescriptionIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="5" y="4.5" width="14" height="15" rx="2.2" />
    <path d="M8.5 8h7M8.5 11.3h7M8.5 14.6h4.6" />
    <path d="M16.5 14.6v4.2M14.4 16.7h4.2" />
  </BaseIcon>
)

export const CertificateIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="4.5" y="4.5" width="15" height="13.5" rx="2.2" />
    <path d="m8.2 11 2.2 2.1 4.1-4.1" />
    <path d="m10.2 18 1.8 2.8 1.8-2.8" />
  </BaseIcon>
)

/** Escudo com verificação (estado de certificado ativo). */
export const ShieldCheckIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 3.2 5 6v5.5c0 4.2 3.2 8 7.5 9 4.3-1 7.5-4.8 7.5-9V6l-7-2.8z" />
    <path d="m9.5 12 2 2 3.5-3.5" />
  </BaseIcon>
)

export const CalendarClockIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M8 3.5v3M16 3.5v3M3.5 9.5h17" />
    <circle cx="12" cy="15.2" r="3.2" />
    <path d="M12 13.8v1.7l1.2.9" />
  </BaseIcon>
)

export const HeatmapIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="4" y="4" width="5" height="5" rx="1.1" />
    <rect x="10.5" y="4" width="5" height="5" rx="1.1" />
    <rect x="17" y="4" width="3" height="5" rx="1.1" />
    <rect x="4" y="10.5" width="7" height="5" rx="1.1" />
    <rect x="12.5" y="10.5" width="7.5" height="5" rx="1.1" />
    <rect x="4" y="17" width="4" height="3" rx="1.1" />
    <rect x="9.5" y="17" width="10.5" height="3" rx="1.1" />
  </BaseIcon>
)

export const SettingsIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="12" r="3.1" />
    <path d="M19.1 10.7a1 1 0 0 0 .2-1.1l-.8-1.8a1 1 0 0 0-1-.6l-1.2.1a6.9 6.9 0 0 0-1.1-.6l-.4-1.1a1 1 0 0 0-.9-.7h-2a1 1 0 0 0-.9.7l-.4 1.1c-.4.2-.7.4-1.1.6l-1.2-.1a1 1 0 0 0-1 .6l-.8 1.8a1 1 0 0 0 .2 1.1l.9.8a5.6 5.6 0 0 0 0 1.3l-.9.8a1 1 0 0 0-.2 1.1l.8 1.8a1 1 0 0 0 1 .6l1.2-.1c.3.2.7.4 1.1.6l.4 1.1a1 1 0 0 0 .9.7h2a1 1 0 0 0 .9-.7l.4-1.1.6-.3.5-.3 1.2.1a1 1 0 0 0 1-.6l.8-1.8a1 1 0 0 0-.2-1.1l-.9-.8c0-.2.1-.4.1-.7 0-.2 0-.4-.1-.6z" />
  </BaseIcon>
)

/** Ícone de menu (três traços horizontais). */
export const MenuIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M4 6h16" />
    <path d="M4 12h16" />
    <path d="M4 18h16" />
  </BaseIcon>
)

export const ChevronLeftIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="m14.5 18-6-6 6-6" />
  </BaseIcon>
)

export const ChevronRightIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="m9.5 18 6-6-6-6" />
  </BaseIcon>
)

/** Ícone de seta para baixo (dropdown). */
export const ChevronDownIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="m6 9 6 6 6-6" />
  </BaseIcon>
)

/** Ícone de seta para cima (navegação para nova página). */
export const ChevronUpIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="m6 15 6-6 6 6" />
  </BaseIcon>
)

export const PaperclipIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M10 13.5 15.3 8.2a3 3 0 1 1 4.2 4.2l-6.2 6.2a5 5 0 0 1-7.1-7.1l6.3-6.3" />
  </BaseIcon>
)

export const FileTextIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M8 3.5h6l3.5 3.5V20a1.8 1.8 0 0 1-1.8 1.8H8A1.8 1.8 0 0 1 6.2 20V5.3A1.8 1.8 0 0 1 8 3.5z" />
    <path d="M14 3.5V7h3.5" />
    <path d="M9 12h6M9 15.2h6M9 18.2h4.2" />
  </BaseIcon>
)

export const BookIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M4 4.5h5.5c1.6 0 3 1.1 3 2.5v13c0-1.4-1.4-2.5-3-2.5H4v-13z" />
    <path d="M20 4.5h-5.5c-1.6 0-3 1.1-3 2.5v13c0-1.4 1.4-2.5 3-2.5H20v-13z" />
  </BaseIcon>
)

export const ImageIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="4" y="5" width="16" height="14" rx="2.5" />
    <path d="m8.2 15.5 2.9-3.1 3.3 3.6 2.3-2.5 3.1 3.4" />
    <circle cx="9" cy="10" r="1.2" />
  </BaseIcon>
)

export const DownloadIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 3.8v10" />
    <path d="m8.5 10.5 3.5 3.5 3.5-3.5" />
    <path d="M5 20h14" />
  </BaseIcon>
)

export const ShareIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="6.5" cy="12" r="2" />
    <circle cx="17.5" cy="6" r="2" />
    <circle cx="17.5" cy="18" r="2" />
    <path d="M8.2 11.2 15.8 6.8M8.2 12.8l7.6 4.4" />
  </BaseIcon>
)

export const StethoscopeIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M4.8 13V8.5a4.2 4.2 0 0 1 4.2-4.2h0a4.2 4.2 0 0 1 4.2 4.2V13" />
    <path d="M4.8 8.5H7M13.2 8.5h-2.2" />
    <circle cx="9" cy="16" r="3" />
    <path d="M17 11v2.5a4.5 4.5 0 0 1-4.5 4.5h-.5" />
    <circle cx="17" cy="9" r="1.5" />
  </BaseIcon>
)

export const PersonIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="7.5" r="3.5" />
    <path d="M4.5 20.5c0-4.1 3.4-7.5 7.5-7.5s7.5 3.4 7.5 7.5" />
  </BaseIcon>
)

/** Ícone de insight/IA (estrelas) para funcionalidades de resumo com IA. */
export const SparklesIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="m12 3 1.5 3 3 1.5-3 1.5L12 12l-1.5-3L7.5 7.5l3-1.5L12 3Z" />
    <path d="m5 16 .6 1.2 1.2.6-1.2.6L5 19.6l-.6-1.2-1.2-.6 1.2-.6L5 16Z" />
    <path d="m19 16 .6 1.2 1.2.6-1.2.6L19 19.6l-.6-1.2-1.2-.6 1.2-.6L19 16Z" />
  </BaseIcon>
)

/** Ícone de copiar (duas folhas sobrepostas). */
export const CopyIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </BaseIcon>
)

/** Ícone de enviar (seta). */
export const SendIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </BaseIcon>
)

/** Ícone de microfone para comando de voz (ativo/gravando). */
export const MicIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <path d="M12 19v3" />
    <path d="M9 22h6" />
  </BaseIcon>
)

/** Ícone de microfone desligado/parado. */
export const MicOffIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 19v3" />
    <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" />
    <path d="M16.95 16.95A7 7 0 0 1 5 12v-2" />
    <path d="M18.89 13.23A7 7 0 0 0 19 12v-2" />
    <path d="m2 2 20 20" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
  </BaseIcon>
)

/** Ícone de cérebro/IA (cérebro com leve referência a insight). */
export const BrainIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.472 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
    <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.472 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
  </BaseIcon>
)

/** Ícone de raio/atalho (velocidade, ações rápidas). */
export const ZapIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
  </BaseIcon>
)

/** Ícone de logout (sair da conta). */
export const LogOutIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </BaseIcon>
)

/** Ícone de sincronizar/atualizar (setas circulares). */
export const SyncIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
    <path d="M16 21h5v-5" />
  </BaseIcon>
)

/** Ícone de fechar (X). */
export const CloseIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </BaseIcon>
)

/** Ícone de check/sucesso (círculo com check). */
export const CheckCircleIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="m9 12 2 2 4-4" />
  </BaseIcon>
)

/** Ícone de hospital/plantão (cruz médica em círculo). */
export const HospitalCrossIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 5v14M7 12h10" />
  </BaseIcon>
)

/** Ícone de fixar/pin (push pin para fixar card). */
export const PinIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 2v5l4 4-2 8-2-8 4-4V2" />
    <circle cx="12" cy="2" r="1.5" />
  </BaseIcon>
)

/** Ícone de local/pin no mapa. */
export const MapPinIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 2a6 6 0 0 1 6 6c0 4.5-6 10-6 10s-6-5.5-6-10a6 6 0 0 1 6-6Z" />
    <circle cx="12" cy="8" r="2" />
  </BaseIcon>
)

/** Ícone de paciente com prontuário/exame (pessoa + documento). */
export const UserFileIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="8.5" cy="6.5" r="2.5" />
    <path d="M3 19.5a5.5 5.5 0 0 1 11 0" />
    <path d="M14 5.5h4.5l2.5 2.5v9a1.2 1.2 0 0 1-1.2 1.2H14a1.2 1.2 0 0 1-1.2-1.2V5.5z" />
    <path d="M14 5.5v2.5h2.5" />
  </BaseIcon>
)

/** Ícone de calculadora para doses de medicamentos. */
export const CalculatorIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <path d="M8 7h8M8 11h2M12 11h2M16 11h2M8 15h2M12 15h2M16 15h2M8 19h8" />
  </BaseIcon>
)

/** Ícone de etiqueta/tag. */
export const TagIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M4 6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v10l-5-2.5L4 16V6Z" />
    <path d="M10 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" />
  </BaseIcon>
)

/** Ícone de editar/lápis. */
export const PencilIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M17 3a2.8 2.8 0 0 1 2 2l-1 4-5 5-4 1-1-4 5-5 4-1Z" />
    <path d="M15 5 19 9" />
  </BaseIcon>
)

/** Ícone de impressora (impressão). */
export const PrinterIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M6 9V2h12v7" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <path d="M6 14h12v8H6z" />
  </BaseIcon>
)

/** Ícone de coroa (licença Pro/Premium). */
export const CrownIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 2l2 6h4l-3 4 1 6-4-4-4 4 1-6-3-4h4l2-6z" />
  </BaseIcon>
)

/** Ícone de três pontos verticais (menu de opções). */
export const EllipsisVerticalIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="6" r="1.2" />
    <circle cx="12" cy="12" r="1.2" />
    <circle cx="12" cy="18" r="1.2" />
  </BaseIcon>
)

/** Ícone de QR Code / escanear. */
export const QrCodeIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="4" height="4" rx="0.5" />
    <rect x="9" y="14" width="4" height="4" rx="0.5" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <rect x="16" y="16" width="3" height="3" rx="0.5" />
  </BaseIcon>
)

/** Ícone de sol (light mode). */
export const SunIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" strokeWidth="2" strokeLinecap="round" />
  </BaseIcon>
)

/** Ícone de lua (dark mode). */
export const MoonIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </BaseIcon>
)

/** Ícone de filtro (sliders). */
export const FilterIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M4 6h16M4 12h10M4 18h6" strokeWidth="2" strokeLinecap="round" />
  </BaseIcon>
)

/** Ícone de seringa/injeção (aplicação). */
export const SyringeIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M10 4h4v2h-4z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 8h6v12H9z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 20v2" strokeWidth="1.5" strokeLinecap="round" />
  </BaseIcon>
)

/** Ícone de monitor (system theme). */
export const MonitorIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" strokeWidth="2" strokeLinecap="round" />
  </BaseIcon>
)

/** Ícone de câmera. */
export const CameraIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </BaseIcon>
)

/** Ícone de sino/notificação. */
export const BellIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </BaseIcon>
)

/** Sino desativado — indicador não-cor em lembretes (A11Y-017). */
export const BellOffIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="m2 2 20 20" />
  </BaseIcon>
)

/** Ícone de mensagem/feedback (balão de fala). */
export const MessageSquareIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </BaseIcon>
)

/** Balão de chat com "+" — nova conversa. */
export const MessageSquarePlusIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <path d="M12 7v6M9 10h6" />
  </BaseIcon>
)

/** Alto-falante — ouvir / TTS. */
export const VolumeIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M11 5 6 9H2v6h4l5 4V5z" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </BaseIcon>
)

/** Ícone de check (marcar como lido). */
export const CheckIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M20 6 9 17l-5-5" />
  </BaseIcon>
)

/** Cifrão — status de pagamento pendente em plantões. */
export const CurrencySignIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 2v20" />
    <path d="M15.5 7.5A3.5 3.5 0 0 0 12 5.5 3.5 3.5 0 0 0 8.5 9c0 2 1.5 2.8 3.5 3.5 2 .7 3.5 1.5 3.5 3.5a3.5 3.5 0 0 1-6 2.5" />
  </BaseIcon>
)

/** Ícone de lixeira (excluir/limpar). */
export const TrashIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M10 11v6M14 11v6" />
  </BaseIcon>
)

/** Ícone de seta para cima (tendência positiva). */
export const ArrowUpIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 19V5" />
    <path d="m5 12 7-7 7 7" />
  </BaseIcon>
)

/** Ícone de seta para baixo (tendência negativa). */
export const ArrowDownIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 5v14" />
    <path d="m5 12 7 7 7-7" />
  </BaseIcon>
)

/** Ícone de importação/upload (seta para cima com arquivo). */
export const ArrowUpTrayIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <path d="M12 3v12" />
  </BaseIcon>
)

/** Ícone de link externo (abrir em nova aba). */
export const ExternalLinkIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </BaseIcon>
)

/** Ícone de feed RSS / notícias. */
export const RssIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M4 11a9 9 0 0 1 9 9" />
    <path d="M4 4a16 16 0 0 1 16 16" />
    <circle cx="5" cy="19" r="1" fill="currentColor" stroke="none" />
  </BaseIcon>
)

/** Ícone de cronômetro/timer (controle de turno). */
export const TimerIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2.5 1.5" />
    <path d="M10 2h4" />
    <path d="M12 2v2" />
  </BaseIcon>
)

/** Ícone de parar/stop (encerrar turno). */
export const StopCircleIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="12" r="9" />
    <rect x="9" y="9" width="6" height="6" rx="0.5" fill="currentColor" stroke="none" />
  </BaseIcon>
)

/** Caixas / estoque (medicamentos, vacinas). */
export const PackageIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="M3.27 6.96 12 12.01l8.73-5.05" />
    <path d="M12 22.08V12" />
  </BaseIcon>
)

/** Chave (passkey / WebAuthn). */
export const KeyRoundIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h2.172a2 2 0 0 0 1.414-.586l.828-.828a2 2 0 0 1 2.828 0l1.172 1.172a2 2 0 0 0 2.828 0l2.172-2.172a2 2 0 0 0 0-2.828l-1.172-1.172a2 2 0 0 1 0-2.828l.828-.828a2 2 0 0 0 .586-1.414V8a2 2 0 0 0-2-2h-2.172a2 2 0 0 0-1.414.586l-.828.828a2 2 0 0 1-2.828 0L9.172 5.172a2 2 0 0 0-2.828 0L4.172 7.344a2 2 0 0 0 0 2.828l1.172 1.172a2 2 0 0 1 0 2.828l-.828.828z" />
    <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" stroke="none" />
  </BaseIcon>
)

/** Wi-Fi desligado — estado offline sem depender só de cor. */
export const WifiOffIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 20h.01" />
    <path d="M8.5 16.429a5 5 0 0 1 7 0" />
    <path d="M5 12.859a10 10 0 0 1 5.17-2.69" />
    <path d="M19 12.859a10 10 0 0 0-2.007-1.523" />
    <path d="M2 8.82a15 15 0 0 1 4.177-2.643" />
    <path d="M22 8.82a15 15 0 0 0-11.288-3.764" />
    <path d="m2 2 20 20" />
  </BaseIcon>
)

/** Ícone de olho aberto — exibir valor (toggle estilo senha de app bancário). */
export const EyeIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </BaseIcon>
)

/** Ícone de olho riscado — ocultar/mascarar valor. */
export const EyeOffIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M9.9 5.6A10.4 10.4 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a13.9 13.9 0 0 1-3.1 3.9" />
    <path d="M6.5 7.6C4 9.3 2.5 12 2.5 12s3.5 6.5 9.5 6.5c1.3 0 2.5-.3 3.5-.8" />
    <path d="M9.9 14.1a3 3 0 0 0 4.2-4.2" />
    <path d="m2 2 20 20" />
  </BaseIcon>
)


/** Ícone de histórico (relógio com seta de retorno). */
export const HistoryIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l3.5 2" />
  </BaseIcon>
)

/** Ícone de modelo/template (layout com blocos). */
export const LayoutTemplateIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="3" y="3" width="18" height="7" rx="1.5" />
    <rect x="3" y="14" width="9" height="7" rx="1.5" />
    <rect x="16" y="14" width="5" height="7" rx="1.5" />
  </BaseIcon>
)

/** Figura humana em pé — atlas / anatomia. */
export const PersonStandingIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <circle cx="12" cy="5.2" r="1.8" />
    <path d="M9.2 20.5 12 14.2l2.8 6.3" />
    <path d="M6.2 8.8 12 7l5.8 1.8" />
    <path d="M12 7v7.2" />
  </BaseIcon>
)

export const RepeatIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="m17 2 4 4-4 4" />
    <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
    <path d="m7 22-4-4 4-4" />
    <path d="M21 13v1a4 4 0 0 1-4 4H3" />
  </BaseIcon>
)

/** Balão de conversa (WhatsApp) — o app usa o ícone genérico, sem a marca. */
export const MessageCircleIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M21 11.5a8.4 8.4 0 0 1-12.4 7.4L3 21l2.1-5.5A8.4 8.4 0 1 1 21 11.5z" />
  </BaseIcon>
)

export const PillIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
    <path d="m8.5 8.5 7 7" />
  </BaseIcon>
)

export const FlaskIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M9 3h6M10 3v6L4.6 19a2 2 0 0 0 1.8 3h11.2a2 2 0 0 0 1.8-3L14 9V3" />
    <path d="M7 15h10" />
  </BaseIcon>
)

export const LinkIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </BaseIcon>
)

export const PenIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </BaseIcon>
)

/** Marcador «Salvar para depois» (notícias); `filled` quando salvo. */
export const BookmarkIcon = ({ filled = false, ...props }: IconProps & { filled?: boolean }) => (
  <BaseIcon {...props}>
    <path d="M6 3h12v18l-6-4-6 4z" fill={filled ? 'currentColor' : 'none'} />
  </BaseIcon>
)

export const PhoneIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
  </BaseIcon>
)

export const GiftIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M12 8v13" />
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5" />
  </BaseIcon>
)

export const ChevronsUpDownIcon = (props: IconProps) => (
  <BaseIcon {...props}>
    <path d="m7 15 5 5 5-5" />
    <path d="m7 9 5-5 5 5" />
  </BaseIcon>
)
